/* eslint-disable no-param-reassign */
import { mapObject, chain, isArray } from 'underscore';
import { cloneDeep } from '@bingads-webui-universal/primitive-utilities';
import {
  DATA_SOURCE_KEY_MAPPING,
  mailIcon,
  mapPinIcon,
  phoneIcon,
} from '@bingads-webui-campaign-react/smart-component-util';

export const DATA_SOURCE_NAME = 'data-source-name';

const ActionTypes = {
  CallNow: {
    text: _TL_('Call now'),
    uriPrefix: 'tel:',
    dataSourceName: DATA_SOURCE_KEY_MAPPING.PhoneNumber,
    editorLabel: _TL_('Phone number'),
    icon: phoneIcon,
    iconText: _TL_('Call'),
    enable: true,
  },
  SendMail: {
    text: _TL_('Send email'),
    uriPrefix: 'mailto:',
    dataSourceName: DATA_SOURCE_KEY_MAPPING.Emails,
    readProcessor: value => (isArray(value) ? value[0] : value),
    writeProcessor: value => [value],
    editorLabel: _TL_('Email address'),
    icon: mailIcon,
    enable: true,
  },
  GetDirections: {
    text: _TL_('Get Directions'),
    uriPrefix: 'https://maps.bing.com/?q=',
    dataSourceName: DATA_SOURCE_KEY_MAPPING.LocationInfo,
    editorLabel: _TL_('Address'),
    icon: mapPinIcon,
    iconText: _TL_('Directions'),
  },
  GetQuote: {
    text: _TL_('Get quote'),
    isFormLink: true,
    dataSourceName: DATA_SOURCE_KEY_MAPPING.Emails,
    readProcessor: value => (isArray(value) ? value[0] : value),
    writeProcessor: value => [value],
    editorLabel: _TL_('Email address'),
    enable: true,
  },
  ContactUs: {
    text: _TL_('Contact us'),
    isFormLink: true,
    dataSourceName: DATA_SOURCE_KEY_MAPPING.Emails,
    readProcessor: value => (isArray(value) ? value[0] : value),
    writeProcessor: value => [value],
    editorLabel: _TL_('Email address'),
    enable: true,
  },
  BookAppointment: {
    text: _TL_('Book appointment'),
    isLinkEditable: true,
    enable: true,
    dataSourceName: DATA_SOURCE_KEY_MAPPING.Emails,
    readProcessor: value => (isArray(value) ? value[0] : value),
    writeProcessor: value => [value],
    editorLabel: _TL_('Email address'),
    operations: [
      {
        key: 'hours',
        title: _TL_('Manage appointment availability'),
        action: _TL_('Submit'),
      }, {
        key: 'results',
        title: _TL_('Manage appointments'),
        action: _TL_('Edit'),
      },
    ],
  },
  ShopNow: {
    text: _TL_('Shop now'),
    isLinkEditable: true,
  },
  SignUp: {
    text: _TL_('Sign up'),
    isLinkEditable: true,
  },
  OrderFood: {
    text: _TL_('Order food'),
    isLinkEditable: true,
  },
  PurchaseGiftCard: {
    text: _TL_('Purchase gift card'),
    isLinkEditable: true,
  },
  CustomLink: {
    text: _TL_('Create your own'),
    isTextEditable: true,
    isLinkEditable: true,
    enable: true,
  },
};

export const SmartActionButtonTypes = mapObject(ActionTypes, (val, key) => ({
  ...val,
  name: key,
}));

// These actions would show email address
export const EMAIL_ACTION_TYPES = [
  SmartActionButtonTypes.SendMail.name,
  SmartActionButtonTypes.GetQuote.name,
  SmartActionButtonTypes.ContactUs.name,
  SmartActionButtonTypes.BookAppointment.name,
];

// Will mutate the original object
export const applyBookAppointmentDefaultConfig = (config, pilots) => {
  config.actionType = SmartActionButtonTypes.BookAppointment.name;
  delete config.linkText;

  if (pilots.isSmartPageBookAppointmentEnabled) {
    config[DATA_SOURCE_NAME] = DATA_SOURCE_KEY_MAPPING.Emails;
  } else {
    delete config[DATA_SOURCE_NAME];
  }

  config.linkUrl = '';
};

/**
 * Update CTA(Call to action) component config by action type
 * @param {String} actionType Action Type
 * @param {Object} componentConfig Call to action component config
 * @param {Object} i18n i18n
 * @param {Object} pilots pilots
 * @returns {Object} A new component config object.
 */
export const getUpdatedComponentConfigByType = (actionType, componentConfig = {}, i18n, pilots) => {
  const newComponentConfig = cloneDeep(componentConfig);
  newComponentConfig.actionType = actionType;

  switch (actionType) {
    case 'CallNow':
      newComponentConfig[DATA_SOURCE_NAME] = DATA_SOURCE_KEY_MAPPING.PhoneNumber;
      delete newComponentConfig.linkText;
      delete newComponentConfig.linkUrl;
      break;
    case 'GetDirections':
      newComponentConfig[DATA_SOURCE_NAME] = DATA_SOURCE_KEY_MAPPING.LocationInfo;
      delete newComponentConfig.linkText;
      delete newComponentConfig.linkUrl;
      break;
    case 'SendMail':
    case 'GetQuote':
    case 'ContactUs':
      newComponentConfig[DATA_SOURCE_NAME] = DATA_SOURCE_KEY_MAPPING.Emails;
      delete newComponentConfig.linkText;
      delete newComponentConfig.linkUrl;
      break;
    case 'ShopNow':
    case 'SignUp':
    case 'OrderFood':
    case 'PurchaseGiftCard':
      delete newComponentConfig[DATA_SOURCE_NAME];
      delete newComponentConfig.linkText;
      newComponentConfig.linkUrl = '';
      break;
    case 'BookAppointment':
      applyBookAppointmentDefaultConfig(newComponentConfig, pilots);
      break;
    case 'CustomLink':
      delete newComponentConfig[DATA_SOURCE_NAME];
      newComponentConfig.linkUrl = '';
      newComponentConfig.linkText = i18n.getString(ActionTypes.CustomLink.text);
      break;
    default:
      newComponentConfig.actionType = SmartActionButtonTypes.GetQuote.name;
      newComponentConfig[DATA_SOURCE_NAME] = DATA_SOURCE_KEY_MAPPING.Emails;
      delete newComponentConfig.linkText;
      delete newComponentConfig.linkUrl;
  }

  return newComponentConfig;
};

/**
 * Get available action types.
 * @param {Object} i18n i18n
 * @returns {Array} Available action types
 */
export const getActionOptions = i18n => (chain(SmartActionButtonTypes)
  .pairs()
  .map(item => (item[1].enable && {
    key: item[0],
    text: i18n.getString(item[1].text),
  }))
  .compact()
  .value()
);
